/* eslint-disable operator-linebreak */
/* eslint-disable class-methods-use-this */
import { Method, AxiosError, AxiosResponse, ResponseType, CancelToken } from 'axios';
import sessionStore from 'store/sessionStore';
import { SESSION_START } from 'utils/auth';
import { PROFILE_INACTIVE_MSG } from 'utils/constants';
import aes from '../utils/aes';
import { axiosInstance } from './axiosConfig';

class Api {
  public request<Res = any>(
    path: string,
    postBody: any = {},
    isUrlEncodedData = true,
    method: Method = 'get',
    isDataEncrypted = true,
    responseType?: ResponseType,
    cancelToken?: CancelToken,
  ): Promise<Res> {
    const endpoint = path;
    const headers = {
      'Content-Type': isUrlEncodedData ? 'application/x-www-form-urlencoded;charset=UTF-8' : 'application/json',
    };
    let body: string = isUrlEncodedData ? this.formParams(postBody) : JSON.stringify(postBody);
    if (isDataEncrypted) {
      body = aes.encrypt(body);
    }

    return axiosInstance
      .request({
        url: endpoint,
        data: body,
        method,
        headers,
        responseType,
        cancelToken,
      })
      .then((response: AxiosResponse) => {
        const resData = isDataEncrypted ? JSON.parse(aes.decrypt(response.data)) : response.data;
        if (resData.code === 103) {
          sessionStore.setSessionTimeout(true);
        }
        if (resData.description === PROFILE_INACTIVE_MSG) {
          sessionStore.setProfileActive(false);
        }
        if (resData.code !== 0 && responseType !== 'arraybuffer') {
          throw resData;
        }

        localStorage.setItem(SESSION_START, JSON.stringify(Date.now()));
        return resData as Res;
      })
      .catch((error: AxiosError) => {
        throw error;
      });
  }

  public requestWithFile<Res = any>(
    path: string,
    postBody: any = {},
    isUrlEncodedData: boolean,
    isDataEncrypted: boolean,
    method: Method = 'post',
  ): Promise<Res> {
    const endpoint = path;
    const headers = {
      'Content-Type': 'multipart/form-data',
    };
    const payload = new FormData();

    if (postBody) {
      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const key in postBody) {
        payload.append(key, postBody[key]);
      }
    }

    return axiosInstance
      .request({
        url: endpoint,
        data: payload,
        method,
        headers,
      })
      .then((response: AxiosResponse) => {
        const resData = isDataEncrypted ? JSON.parse(aes.decrypt(response.data)) : response.data;
        if (resData.code === 103) {
          sessionStore.setSessionTimeout(true);
        }
        if (resData.description === PROFILE_INACTIVE_MSG) {
          sessionStore.setProfileActive(false);
        }
        if (resData.code !== 0) {
          throw resData;
        }

        localStorage.setItem(SESSION_START, JSON.stringify(Date.now()));
        return resData as Res;
      })
      .catch((error: AxiosError) => {
        throw error;
      });
  }

  public get<Res = any>(
    path: string,
    postBody: any = {},
    isUrlEncodedData = true,
    isEncrypted = true,
    responseType?: ResponseType,
    cancelToken?: CancelToken,
  ): Promise<Res> {
    return this.request<Res>(path, postBody, isUrlEncodedData, 'get', isEncrypted, responseType, cancelToken);
  }

  public post<Res = any>(
    path: string,
    postBody: any = {},
    isUrlEncodedData = true,
    isEncrypted = true,
    responseType?: ResponseType,
    cancelToken?: CancelToken,
  ): Promise<Res> {
    return this.request<Res>(path, postBody, isUrlEncodedData, 'post', isEncrypted, responseType, cancelToken);
  }

  public put<Res = any>(
    path: string,
    postBody: any = {},
    isUrlEncodedData = true,
    isEncrypted = true,
    responseType?: ResponseType,
    cancelToken?: CancelToken,
  ): Promise<Res> {
    return this.request<Res>(path, postBody, isUrlEncodedData, 'put', isEncrypted, responseType, cancelToken);
  }

  public postWithFiles<Res = any>(
    path: string,
    postBody: any = {},
    isUrlEncodedData: boolean,
    isDataEncrypted: boolean,
    method: Method = 'post',
  ): Promise<Res> {
    return this.requestWithFile<Res>(path, postBody, isUrlEncodedData, isDataEncrypted, method);
  }

  public delete<Res = any>(
    path: string,
    postBody: any = {},
    isUrlEncodedData = true,
    isEncrypted = true,
  ): Promise<Res> {
    return this.request<Res>(path, postBody, isUrlEncodedData, 'delete', isEncrypted);
  }

  // eslint-disable-next-line class-methods-use-this
  private formParams(rawData: Record<string, any>): string {
    const formDataItems: string[] = [];
    buildFormData(formDataItems, rawData);

    function buildFormData(dataItems: string[], data: any, parentKey: string | null = null) {
      if (
        data &&
        typeof data === 'object' &&
        !(data instanceof Date) && // do not loop through datas
        !(data instanceof File) && // do not loop through files
        !(data instanceof Blob) && // do not loop through images and blobs
        !(Array.isArray(data) && !data.length) // do not loop through empty arrays
      ) {
        Object.keys(data).forEach((key) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          buildFormData(dataItems, data[key], parentKey ? `${parentKey}[${key}]` : key);
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const value = data == null ? '' : (data as string);
        const useKey = parentKey as string;
        dataItems.push(`${useKey}=${value}`);
      }
    }

    return formDataItems.join('&');
  }
}

const api = new Api();

export default api;
